import dayjs from 'dayjs';
import { Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  FlatList,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';

import CachedImage from '@components/CachedImage/CachedImage';
import EmptyStateNoTeams from '@components/EmptyState/EmptyStateNoTeams';
import { TeamsModal } from '@components/FormModals/TeamsModal';
import {
  DeferredInvitation,
  InviteMemberModal,
} from '@components/Profile/TeamDetailPage/InviteMemberModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { FadeInView } from '@components/shared/FadeInView';
import Icon from '@components/shared/Icon/Icon';
import {
  SkeletonList,
  TeamListRowSkeleton,
} from '@components/shared/skeletons';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  LocalFile,
  ProjectMemberRole,
  Team,
  TeamMemberRole,
} from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useRefetchOnRemount } from '@hooks/useRefetchOnRemount';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { FormValues } from '@navigation/payments/create-subscription-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { createTeamSchema } from '@schemas/createTeamSchema';
import { getPlanTagContent } from '@screens/Profile/PlanTag.web';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { getStructuralRowBackground } from '@utils/hoverStyles';
import { getTeamStatus } from '@utils/teams/getTeamStatus';
import { getUpgradeCTATitleAndDesc } from '@utils/teams/subscription';

const { width } = Dimensions.get('window');

const columnFlexes =
  width < 1200 ? [1, 0.45, 0.2, 1.1, 0.8] : [0.7, 0.4, 0.2, 1, 1];

type TeamRowContainerProps = {
  onPress: () => void;
  isCanceled?: boolean;
  children: React.ReactNode;
};

// Hoverable wrapper for a team row — extracted so useHover runs per row
// (hooks can't live inside a FlatList renderItem callback). Structural row
// (TA-9005): hover tints to grey01.
const TeamRowContainer = ({
  onPress,
  isCanceled,
  children,
}: TeamRowContainerProps) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <TouchableOpacity onPress={onPress}>
      <Box
        {...hoverProps}
        backgroundColor={getStructuralRowBackground(isHovered)}
        style={[
          styles.rowContainer,
          {
            paddingHorizontal: theme.spacing.xs,
            borderRadius: theme.borderRadii.xs,
            height: isCanceled ? 68 : 48,
          },
        ]}>
        {children}
      </Box>
    </TouchableOpacity>
  );
};

export const AccountTeams: React.FC = () => {
  const { me } = useMe();
  const teamQueryOptions = useMemo(
    () => ({ includePending: false, fetchPolicy: 'cache-first' as const }),
    []
  );
  const {
    teams = [],
    loading,
    refetch,
  } = useListTeamsFromQuery(teamQueryOptions);
  // Not useRefetchOnFocus: this screen is inside FocusedTabContent, which
  // unmounts/remounts on every tab switch — see useRefetchOnRemount's
  // docstring for why the per-instance "skip first focus" hook is a no-op
  // here.
  useRefetchOnRemount('AccountTeams.web:listTeams', refetch);
  // TA-9872: subscriptionDetails was split out of listTeams — read it from
  // the getTeamBilling query instead.
  const { billingByTeamId } = useTeamBillingFromQuery();
  // TA-10280: 3 skeleton rows while the team list fetches on route enter —
  // scoped to "nothing rendered yet" so a background refetch never blanks
  // visible rows, and never lets the empty state flash mid-query.
  const showSkeleton = useMinimumVisible(loading && teams.length === 0);

  const { t } = useTranslation('models');
  const [isHovered, setIsHovered] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [navigatedFromCreateTeam, setNavigatedFromCreateTeam] = useState(false);

  const [primaryTeam, setPrimaryTeam] = useState(me?.primaryTeamId);
  const users = () => {
    if (!me) {
      return [];
    }
    const owner = {
      id: me.id,
      avatar: me.avatar,
      name: me.name,
      firstName: me.firstName,
      lastName: me.lastName,
      role: ProjectMemberRole.Owner,
    };
    return [owner];
  };
  const initialValues: FormValues = {
    users: users(),
    teamId: null,
    plan: '',
    planName: '',
    billingCycle: 'year',
    paymentComplete: false,
    priceId: undefined,
    price: 0,
    location: null,
    name: '',
    skillIds: [],
    logo: null,
  };
  const { setSelectedTeam, setShowPending } = useWebDrawerNavigator();
  const appNavigation = useAppNavigation(); // Use the imported useAppNavigation

  const {
    setIsTeamPersonal,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setUpgradeConfirmModalOpen,
    setTeamValues,
    subscriptionCreatedModalOpen,
  } = useWebDrawer();

  // Team creation is deferred (TA-10009): the backend creates the team from
  // teamAttributes when createStripeSubscription runs after checkout, so
  // abandoning the flow leaves no orphaned team behind.
  const submitForm = (
    values: FormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);

    // Only proceed if name is provided as it is required.
    if (values.name) {
      setUUIdTeamOpen('');
      setShowCreateTeamModal(false);
      setShowMemberModal(true);
      setNavigatedFromCreateTeam(true);
    }

    setIsTeamPersonal(false);
    setTeamValues(values);
    setSubmitting(false);
  };

  useEffect(() => {
    if (me?.primaryTeamId !== primaryTeam) {
      setPrimaryTeam(me?.primaryTeamId);
    }
  }, [me?.primaryTeamId, primaryTeam]);

  useEffect(() => {
    if (subscriptionCreatedModalOpen) {
      refetch();
    }
  }, [subscriptionCreatedModalOpen, refetch]);

  const sectionHeader = () => (
    <Box
      style={[
        styles.rowContainer,
        {
          backgroundColor: theme.colors.grey01,
          paddingHorizontal: theme.spacing.xs,
          borderRadius: theme.borderRadii.xs,
          height: 48,
        },
      ]}>
      <Box flex={columnFlexes[0]} flexDirection='row' alignItems='center'>
        <Box width={20} height={20} marginRight='m' /> {/* Placeholder icon */}
        <Text
          variant={
            width < 1025 ? 'labelEmphasizedResponsive' : 'labelTabHeaderText'
          }>
          TEAM NAME
        </Text>
      </Box>
      <Box
        flex={columnFlexes[1]}
        alignItems='flex-start'
        justifyContent='center'>
        <Text
          variant={
            width < 1025 ? 'labelEmphasizedResponsive' : 'labelTabHeaderText'
          }>
          MY ROLE
        </Text>
      </Box>
      <Box
        flex={columnFlexes[2]}
        alignItems='flex-start'
        justifyContent='center'>
        <Text
          variant={
            width < 1025 ? 'labelEmphasizedResponsive' : 'labelTabHeaderText'
          }>
          MEMBERS
        </Text>
      </Box>
      <Box
        flex={columnFlexes[3]}
        alignItems='flex-start'
        minWidth={width < 1200 ? 160 : 130}
        justifyContent='center'>
        <Text
          style={{ marginLeft: theme.spacing.s }}
          variant={
            width < 1025 ? 'labelEmphasizedResponsive' : 'labelTabHeaderText'
          }>
          PLAN
        </Text>
      </Box>
      <Box
        flex={columnFlexes[4]}
        style={{ paddingLeft: theme.spacing.xs }}
        minWidth={width < 1200 ? 140 : 110}
        alignItems='flex-start'
        justifyContent='center'></Box>
    </Box>
  );

  const renderAvatar = (team) => {
    if (team.avatarFile)
      return (
        <CachedImage
          avatarId={team.avatarFile.id}
          avatarType='team'
          image={team.avatarFile as unknown as LocalFile}
          width={30}
          height={30}
          borderRadius={theme.borderRadii.s}
        />
      );

    if (team?.personal)
      return (
        <Icon
          variant='xxl'
          padding='none'
          name='Office'
          backgroundColor='greenSecondaryMild'
          isCircle={false}
        />
      );
    return (
      <Icon
        variant='xxl'
        width={30}
        height={30}
        padding='none'
        name={(team?.avatar as keyof typeof Icon) || 'TTProject'}
        backgroundColor='black'
        isCircle={false}
      />
    );
  };
  const handleSubscriptionPress = (team: Team, teamStatus) => {
    setSelectedTeam(team);
    setShowPending(team?.pending);
    appNavigation.navigateToMyprofileSubscriptionDetail(team.uuid, teamStatus);
  };
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const goToUpgradeSubscription = (team: Team) => {
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setUpgradeConfirmModalOpen(true);
  };

  const getUserRole = (item: Team) => {
    if (item?.owner?.id === me?.id) return TeamMemberRole.Owner;
    return item?.currentUserRole;
  };

  const renderTeamItem = ({ item: rawItem }) => {
    // TA-9872: merge billing data (split out of listTeams) into the row item
    // so the status helpers keep reading subscriptionDetails as before.
    const item = rawItem && {
      ...rawItem,
      subscriptionDetails: billingByTeamId[rawItem.id]?.subscriptionDetails,
    };
    // Billing loads asynchronously (TA-9872). For non-personal rows the plan
    // tag/CTA derive from subscriptionDetails, so until billing has resolved
    // for this row the tag would flash a wrong (basic) state. Personal rows
    // don't depend on billing. Gate the billing-derived UI on this.
    const rowBillingReady =
      !!item?.personal || billingByTeamId[rawItem.id] !== undefined;
    const status = getUpgradeCTATitleAndDesc(item);
    const type = getTeamStatus(item);
    const isCanceled =
      item?.subscriptionDetails?.isSubscriptionCanceled &&
      item?.subscriptionDetails?.isSubscriptionActive;
    const { icon, text, bgColor, textColor, iconColor } =
      getPlanTagContent(type);

    const needsUpgrade =
      type === 'team-trial' || type === 'team-expired' || !!status?.title;
    const iconName = icon === 'alert-triangle' ? 'alert' : icon;
    if (!item || !item.id) return null;
    return (
      <TeamRowContainer
        onPress={() => handleSubscriptionPress(item, status)}
        isCanceled={!!isCanceled}>
        <Box flex={columnFlexes[0]} flexDirection='row' alignItems='center'>
          {renderAvatar(item)}

          <Text
            variant='webBodySecondary'
            ml='m'
            ellipsizeMode='tail'
            numberOfLines={1}
            style={{ flexShrink: 1, overflow: 'hidden' }}>
            {item.name}
          </Text>
        </Box>
        <Box
          flex={columnFlexes[1]}
          alignItems='flex-start'
          justifyContent='center'>
          <Text variant='webBodySecondary'>
            {item?.personal
              ? capitalizeFirstLetter(getUserRole(item)) +
                ' (Personal Projects)'
              : capitalizeFirstLetter(getUserRole(item))}
          </Text>
        </Box>
        <Box
          flex={columnFlexes[2]}
          alignItems='flex-start'
          justifyContent='center'>
          <Text variant='webBodySecondary'>{item.usersCount}</Text>
        </Box>
        <Box
          flex={columnFlexes[3]}
          flexDirection='column'
          alignItems='flex-start'
          justifyContent='center'
          minWidth={width < 1200 ? 150 : 120}>
          {rowBillingReady && (
            <Box marginTop={isCanceled ? 'xs' : 'none'}>
              <Box style={[styles.tagContainer, { backgroundColor: bgColor }]}>
                <Icon
                  variant='xs'
                  name={iconName}
                  color={iconColor}
                  marginRight='xxs'
                />

                <Text
                  style={[
                    styles.tagText,
                    {
                      color: textColor,
                      fontSize: 12,
                    },
                  ]}
                  numberOfLines={1}
                  ellipsizeMode='tail'>
                  {text}
                </Text>
              </Box>
            </Box>
          )}
          <Box>
            {isCanceled && (
              <Text
                variant='webBodySecondary'
                color='grey04'
                marginBottom='xxs'
                marginTop='xxs'
                style={{ maxWidth: '100%', flexShrink: 1 }}>
                {`You'll keep access until ${dayjs(
                  item?.subscriptionDetails?.expirationDate
                ).format('MMM DD, YYYY')}`}
              </Text>
            )}
          </Box>
        </Box>
        <Box
          flex={columnFlexes[4]}
          flexDirection='row'
          alignItems='center'
          justifyContent='flex-start'
          minWidth={width < 1200 ? 130 : 100}
          paddingLeft='xs'>
          <Box>
            {rowBillingReady &&
              needsUpgrade &&
              item.currentUserRole !== TeamMemberRole.Member && (
                <TouchableOpacity onPress={() => goToUpgradeSubscription(item)}>
                  <Text
                    style={[
                      styles.upgradeText,
                      {
                        marginLeft: 0,
                        fontSize: width < 1200 ? 12 : 14,
                      },
                    ]}
                    numberOfLines={1}
                    textDecorationLine='underline'
                    ellipsizeMode='tail'>
                    {status?.title ?? 'Upgrade Now'}
                  </Text>
                </TouchableOpacity>
              )}
          </Box>
        </Box>
      </TeamRowContainer>
    );
  };

  return (
    <Box flex={1}>
      {!showSkeleton && teams.length === 0 ? (
        <EmptyStateNoTeams />
      ) : (
        <>
          <Box
            flexDirection='row-reverse'
            marginHorizontal='s'
            marginBottom='m'>
            <Button
              accessibilityLabel='Create Team'
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              isSmall
              height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
              backgroundColor={isHovered ? 'grey05' : 'black'}
              prefix={<Icon variant='s' name='Plus' color='white' />}
              onPress={() => {
                setUUIdTeamOpen('');
                setIdTeamOpen('');
                setShowCreateTeamModal(true);
              }}
              borderRadius='xs'
              borderColor={isHovered ? 'grey05' : 'black'}
              variant='create'
              marginRight='l'
              prefixMarginRight='xs'
              paddingHorizontal='m'
              paddingVertical='xs'>
              Create Team
            </Button>
          </Box>
          {sectionHeader()}
          {showSkeleton ? (
            <Box marginTop='m'>
              <SkeletonList
                row={(index) => <TeamListRowSkeleton key={index} />}
              />
            </Box>
          ) : (
            <FadeInView style={{ flex: 1 }}>
              <FlatList
                data={teams}
                keyExtractor={(item, index) => item?.id ?? `${index}`}
                renderItem={renderTeamItem}
                ListHeaderComponent={() => <Box marginTop='m' />}
                ItemSeparatorComponent={() => (
                  <Box
                    height={2}
                    backgroundColor='grey02'
                    marginStart='s'
                    marginEnd='l'
                  />
                )}
              />
            </FadeInView>
          )}
          {showCreateTeamModal && (
            <Formik
              initialValues={initialValues}
              validationSchema={createTeamSchema}
              onSubmit={(values, { setSubmitting }) =>
                submitForm(values, setSubmitting)
              }>
              {() => (
                <TeamsModal
                  title='Team Setup'
                  buttonLabel={t('shared:next')}
                  onClose={() => setShowCreateTeamModal(false)}
                />
              )}
            </Formik>
          )}
          <InviteMemberModal
            requireMemberSelection={false}
            isVisible={showMemberModal || false}
            // No team exists yet (deferred creation) — cancelling just drops
            // the locally collected values, nothing to delete
            onCancel={() => {
              setShowMemberModal(false);
              setNavigatedFromCreateTeam(false);
            }}
            onClose={() => {
              setShowMemberModal(false);
              setNavigatedFromCreateTeam(false);
            }}
            teamId=''
            onDeferredSend={(members: DeferredInvitation[]) => {
              // Invitations ride along in teamValues and are sent as
              // teamAttributes.teamMembers by createStripeSubscription
              setTeamValues((prev: FormValues) => ({
                ...prev,
                teamMembers: members,
              }));
              if (navigatedFromCreateTeam) {
                setUpgradeConfirmModalOpen(true);
              }
            }}
          />
          {/* The post-subscription success modal is the shared
              SubscriptionCreatedModal rendered by MainPanel — the local
              congratulations modal that used to live here was unreachable
              (its visibility flag was never set) and has been removed */}
        </>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: '98%',
  },
  tagText: {
    fontWeight: '400',
    textAlignVertical: 'center',
  },
  upgradeText: {
    fontWeight: '500',
    color: theme.colors.greenSecondary,
  },
  tagContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.xxs,
    borderRadius: theme.spacing.xxs,
    maxWidth: '100%',
  },
});
